/**
 * The evaluator's settings and the state of the data.
 *
 * An admin can change the settings and reset them; everyone else sees them read-only, so
 * the people using the evaluator know what it is working on. Every save is a new version;
 * an evaluation keeps the version it used. The save bar appears only once something has
 * changed.
 */

import { type FormEvent, type ReactNode, useCallback, useEffect, useState } from "react";
import { Bot, Check, Database, KeyRound, LogOut, Minus, Plus, RefreshCw, RotateCcw } from "lucide-react";
import { CONDITION_LABEL, CONDITIONS, type Settings, SETTING_LIMITS } from "@ohc-price/shared";
import { ApiError, evaluateApi, type SettingsVersion, type SourceStatus, type User } from "./api";
import { ago, sgTime } from "./format";
import { Callout, Chip, cx, Skeleton, Spinner } from "./ui";

type NumberKey = Exclude<keyof Settings, "conditionDiscounts" | "buyingRules">;
type Setter = <K extends keyof Settings>(key: K, value: Settings[K]) => void;

export function SettingsPage({ user, onError, onLogout }: { user: User; onError: (e: unknown) => void; onLogout: () => void }) {
  const [version, setVersion] = useState<SettingsVersion | null>(null);
  const [form, setForm] = useState<Settings | null>(null);
  const [flags, setFlags] = useState<{ ai: boolean; sales: boolean } | null>(null);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const editable = user.isAdmin;

  useEffect(() => {
    evaluateApi.settings().then((r) => {
      setVersion(r.settings);
      setForm(r.settings.values);
      setFlags({ ai: r.aiAvailable, sales: r.salesAvailable });
    }).catch(onError);
  }, [onError]);

  const dirty = !!form && !!version && JSON.stringify(form) !== JSON.stringify(version.values);

  async function save(event?: FormEvent) {
    event?.preventDefault();
    if (!form) return;
    setBusy(true);
    setError("");
    setMessage("");
    try {
      const r = await evaluateApi.saveSettings(form);
      setVersion(r.settings);
      setForm(r.settings.values);
      setMessage(`Saved as version ${r.settings.id}`);
    } catch (e) {
      onError(e);
      setError(e instanceof ApiError ? e.message : "Could not save.");
    } finally {
      setBusy(false);
    }
  }

  async function reset() {
    if (!window.confirm("Put every number back to its default? Your written buying rules are kept.")) return;
    setBusy(true);
    setError("");
    try {
      const r = await evaluateApi.resetSettings();
      setVersion(r.settings);
      setForm(r.settings.values);
      setMessage(`Reset to defaults, version ${r.settings.id}`);
    } catch (e) {
      onError(e);
      setError(e instanceof ApiError ? e.message : "Could not reset.");
    } finally {
      setBusy(false);
    }
  }

  const set: Setter = (key, value) => {
    if (!form) return;
    setMessage("");
    setForm({ ...form, [key]: value });
  };

  return (
    <div className={cx("space-y-4 px-4 pt-4", dirty ? "pb-28" : "pb-6")}>
      <div>
        <h1 className="text-xl font-semibold tracking-tight text-slate-900">Settings</h1>
        <p className="text-sm text-slate-500">What the evaluator works on, and how fresh the data is.</p>
      </div>

      <Updates user={user} onError={onError} />

      {!form || !version ? (
        <div className="card space-y-3 p-4"><Skeleton className="h-5 w-1/3" /><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>
      ) : (
        <form onSubmit={save} className="space-y-4">
          <div className="flex flex-wrap items-center gap-1.5 px-1 text-xs text-slate-500">
            <Chip tone="navy">Version {version.id}</Chip>
            <span>{version.reset ? "Defaults" : "Changed"}{version.createdBy ? ` by ${version.createdBy}` : ""}, {sgTime(version.createdAt)}</span>
          </div>
          {!editable && <Callout kind="info">Only an admin can change these.</Callout>}
          {flags && (!flags.ai || !flags.sales) && (
            <Callout kind="warn">
              {!flags.ai && "The AI view is off: there is no OpenAI key on the server. "}
              {!flags.sales && "Sold sales are off: there is no browser on the server."}
            </Callout>
          )}

          <Group title="Market value" hint="How the value is worked out from sold sales">
            <Stepper k="lookbackDays" label="Sales lookback" unit="days" help="Older sales are used only when there are too few newer ones" form={form} set={set} editable={editable} limits={SETTING_LIMITS.lookbackDays} />
            <Stepper k="comparables" label="Comparable sales" unit="sales" help="How many of the newest matching sales make the value" form={form} set={set} editable={editable} limits={SETTING_LIMITS.comparables} />
            <Stepper k="minSample" label="Minimum sample" unit="sales" help="Fewer and the value is flagged as resting on too little" form={form} set={set} editable={editable} limits={SETTING_LIMITS.minSample} />
          </Group>

          <Group title="Buying" hint="Maximum offer = market value less this. The market value itself does not change">
            <Stepper k="purchaseDiscountShow" label="At a trade show" unit="%" help={`Pay up to ${100 - form.purchaseDiscountShow}% of market value`} form={form} set={set} editable={editable} limits={SETTING_LIMITS.purchaseDiscountShow} step={5} />
            <Stepper k="purchaseDiscountOnline" label="Online" unit="%" help={`Pay up to ${100 - form.purchaseDiscountOnline}% of market value: Carousell, Telegram groups`} form={form} set={set} editable={editable} limits={SETTING_LIMITS.purchaseDiscountOnline} step={5} />
          </Group>

          <Group title="Condition, raw cards" hint="Taken off the near mint value. No price source splits raw sales by condition, so this is your own judgement">
            <div className="grid grid-cols-5 gap-2 py-3">
              {CONDITIONS.map((c) => (
                <label key={c} className="text-center" title={CONDITION_LABEL[c]}>
                  <div className="text-[13px] font-semibold text-slate-700">{c}</div>
                  <div className="relative mt-1">
                    <input
                      type="number"
                      inputMode="numeric"
                      min={0}
                      max={95}
                      disabled={!editable}
                      className="field num h-11 px-1 pr-5 text-center"
                      value={form.conditionDiscounts[c]}
                      onChange={(e) => set("conditionDiscounts", { ...form.conditionDiscounts, [c]: Number(e.target.value) })}
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-xs text-slate-400">%</span>
                  </div>
                </label>
              ))}
            </div>
          </Group>

          <Group title="Our buying rules" hint="In your own words. The AI reads these with every evaluation. Reset to defaults keeps them" icon={<Bot className="h-4 w-4" />}>
            <textarea
              rows={7}
              maxLength={4000}
              disabled={!editable}
              className="field my-3 min-h-[168px] py-2.5 text-sm leading-relaxed"
              placeholder="For example: we buy PSA 10 only. Slow sellers need a bigger discount. Never buy just before a reprint."
              value={form.buyingRules}
              onChange={(e) => set("buyingRules", e.target.value)}
            />
          </Group>

          <Group title="Schedule" hint="How often the server brings new data in">
            <Stepper k="priceUpdateHours" label="Prices every" unit="hours" help="PriceCharting publishes once a day" form={form} set={set} editable={editable} limits={SETTING_LIMITS.priceUpdateHours} step={6} />
            <Stepper k="cardUpdateDays" label="New cards every" unit="days" help="English card details from TCGdex" form={form} set={set} editable={editable} limits={SETTING_LIMITS.cardUpdateDays} />
          </Group>

          {error && <Callout kind="error">{error}</Callout>}
          {message && !dirty && (
            <div className="flex items-center gap-2 px-1 text-sm font-medium text-emerald-700"><Check className="h-4 w-4" />{message}</div>
          )}
          {editable && (
            <button type="button" disabled={busy} onClick={reset} className="btn-ghost w-full">
              <RotateCcw className="h-4 w-4" />
              Reset to defaults
            </button>
          )}

          {editable && dirty && (
            <div className="fixed inset-x-0 bottom-[64px] z-20 border-t border-slate-200 bg-white/95 backdrop-blur md:bottom-0">
              <div className="mx-auto flex max-w-3xl items-center gap-3 px-4 py-3">
                <span className="flex-1 text-sm font-medium text-slate-700">Unsaved changes</span>
                <button type="button" className="btn-ghost h-11" onClick={() => setForm(version.values)}>Undo</button>
                <button type="submit" disabled={busy} className="btn-primary h-11">
                  {busy && <Spinner />}
                  Save
                </button>
              </div>
            </div>
          )}
        </form>
      )}

      <Account user={user} onLogout={onLogout} onError={onError} />
    </div>
  );
}

/** Who is logged in, changing the password, and logging out. */
function Account({ user, onLogout, onError }: { user: User; onLogout: () => void; onError: (e: unknown) => void }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [again, setAgain] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  async function change(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (next !== again) {
      setError("The two new passwords are not the same");
      return;
    }
    setBusy(true);
    try {
      await evaluateApi.changePassword(current, next);
      setDone(true);
      setOpen(false);
      setCurrent("");
      setNext("");
      setAgain("");
    } catch (e) {
      onError(e);
      setError(e instanceof ApiError ? e.message : "Could not change the password");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="card p-4">
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="truncate font-medium text-slate-900">{user.fullName}</div>
          <div className="text-xs text-slate-500">{user.username}{user.isAdmin ? " · admin" : ""}</div>
        </div>
        <button type="button" onClick={onLogout} className="btn-ghost">
          <LogOut className="h-4 w-4" />
          Log out
        </button>
      </div>
      {done && !open && (
        <div className="mt-3 flex items-center gap-2 text-sm font-medium text-emerald-700"><Check className="h-4 w-4" />Password changed</div>
      )}
      {!open ? (
        <button type="button" onClick={() => { setOpen(true); setDone(false); }} className="mt-3 flex items-center gap-1.5 text-[13px] font-medium text-navy-700">
          <KeyRound className="h-4 w-4" />
          Change password
        </button>
      ) : (
        <form onSubmit={change} className="mt-4 space-y-2.5 border-t border-slate-100 pt-4 animate-fadein">
          <input type="password" autoComplete="current-password" className="field h-11" placeholder="Current password" value={current} onChange={(e) => setCurrent(e.target.value)} />
          <input type="password" autoComplete="new-password" className="field h-11" placeholder="New password, at least 10 characters" value={next} onChange={(e) => setNext(e.target.value)} />
          <input type="password" autoComplete="new-password" className="field h-11" placeholder="New password again" value={again} onChange={(e) => setAgain(e.target.value)} />
          {error && <Callout kind="error">{error}</Callout>}
          <div className="flex gap-2 pt-1">
            <button type="button" className="btn-ghost h-11" onClick={() => setOpen(false)}>Cancel</button>
            <button type="submit" disabled={busy || !current || !next || !again} className="btn-primary h-11 flex-1">
              {busy && <Spinner />}
              Change password
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function Group({ title, hint, icon, children }: { title: string; hint: string; icon?: ReactNode; children: ReactNode }) {
  return (
    <section className="card px-4 pt-4">
      <div className="flex items-center gap-1.5">
        {icon && <span className="text-slate-400">{icon}</span>}
        <div className="label">{title}</div>
      </div>
      <div className="mt-0.5 text-xs text-slate-500">{hint}</div>
      <div className="mt-1 divide-y divide-slate-100">{children}</div>
    </section>
  );
}

function Stepper({
  k, label, unit, help, form, set, editable, limits, step = 1,
}: {
  k: NumberKey;
  label: string;
  unit: string;
  help?: string;
  form: Settings;
  set: Setter;
  editable: boolean;
  limits: readonly [number, number];
  step?: number;
}) {
  const value = form[k];
  const clamp = (n: number) => Math.min(Math.max(n, limits[0]), limits[1]);
  return (
    <div className="flex items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <div className="text-[15px] font-medium text-slate-800">{label}</div>
        {help && <div className="text-xs text-slate-500">{help}</div>}
      </div>
      <div className="flex flex-none items-center gap-1.5">
        {editable && (
          <button type="button" aria-label={`Less ${label}`} onClick={() => set(k, clamp(value - step))} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
            <Minus className="h-4 w-4" />
          </button>
        )}
        <label className="flex w-[74px] items-baseline justify-center gap-0.5 rounded-lg bg-slate-50 py-1.5">
          <input
            type="number"
            inputMode="numeric"
            min={limits[0]}
            max={limits[1]}
            disabled={!editable}
            className="num w-10 bg-transparent text-right text-[15px] font-semibold text-slate-900 outline-none"
            value={value}
            onChange={(e) => set(k, Number(e.target.value))}
            aria-label={label}
          />
          <span className="text-[11px] text-slate-500">{unit}</span>
        </label>
        {editable && (
          <button type="button" aria-label={`More ${label}`} onClick={() => set(k, clamp(value + step))} className="flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 text-slate-600 hover:bg-slate-50">
            <Plus className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

function Updates({ user, onError }: { user: User; onError: (e: unknown) => void }) {
  const [sources, setSources] = useState<SourceStatus[] | null>(null);
  const [running, setRunning] = useState(false);

  const load = useCallback(() => {
    evaluateApi.updates().then((r) => {
      setSources(r.sources);
      setRunning(r.running);
    }).catch(onError);
  }, [onError]);

  useEffect(load, [load]);
  // While updates run, look again every few seconds so the times change on screen.
  useEffect(() => {
    if (!running) return;
    const timer = setInterval(load, 4000);
    return () => clearInterval(timer);
  }, [running, load]);

  const attention = sources?.some((s) => s.stale || s.lastError);

  return (
    <section className="card p-4">
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-1.5">
            <Database className="h-4 w-4 text-slate-400" />
            <div className="label">Data updates</div>
          </div>
          <div className={cx("mt-0.5 text-xs", attention ? "font-medium text-amber-700" : "text-slate-500")}>
            {sources ? (attention ? "Something needs attention" : "Everything is up to date") : "Checking"}
          </div>
        </div>
        {user.isAdmin && (
          <button
            type="button"
            disabled={running}
            onClick={() => evaluateApi.runUpdates().then(() => {
              setRunning(true);
              setTimeout(load, 1500);
            }).catch(onError)}
            className="btn-ghost h-9 px-3 text-[13px]"
          >
            <RefreshCw className={cx("h-4 w-4", running && "animate-spin")} />
            {running ? "Updating" : "Check for updates"}
          </button>
        )}
      </div>
      <ul className="mt-3 divide-y divide-slate-100">
        {(sources ?? []).map((s) => {
          const tone = s.lastError ? "bg-rose-500" : s.stale ? "bg-amber-500" : s.off ? "bg-slate-300" : "bg-emerald-500";
          return (
            <li key={s.source} className="flex gap-3 py-2.5">
              <span className={cx("mt-1.5 h-2 w-2 flex-none rounded-full", tone)} />
              <div className="min-w-0 flex-1">
                <div className="flex items-baseline justify-between gap-2">
                  <span className="text-[13px] font-medium text-slate-800">{s.label}</span>
                  <span className="num flex-none text-[11px] text-slate-500" title={sgTime(s.lastOk)}>{ago(s.lastOk)}</span>
                </div>
                {s.off && <div className="text-[11px] text-slate-500">Not run on this server: {s.off}</div>}
                {s.staleAfterHours === null && <div className="text-[11px] text-slate-500">Brought in by hand</div>}
                {s.stale && <div className="text-[11px] font-medium text-amber-700">Stale: no good update in the last {Math.round(s.staleAfterHours ?? 0)} hours</div>}
                {s.lastError && <div className="text-[11px] text-rose-700">Last try failed: {s.lastError}</div>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}
